// ** React Imports
import { Fragment, useEffect, useState } from 'react'

// ** Next Imports
import { useRouter } from 'next/router'

// ** MUI Imports
import { styled } from '@mui/material/styles'
import Box, { BoxProps } from '@mui/material/Box'

// ** Theme Config Import
import themeConfig from 'src/configs/themeConfig'

// ** Type Import
import { LayoutProps } from 'src/@core/layouts/types'

// ** Components
import Navigation from './components/vertical/navigation'
import Footer from './components/shared-components/footer'
import { Card, useMediaQuery } from '@mui/material'
import Subnavigation from './components/vertical/navigation/SubNavigation'
import { useGlobalState } from '@/src/pages/_app'
import AppBar from './components/vertical/appBar'

// ** Iframe Utils
import { isIframeContext } from '@/src/utils/iframeUtils'

// ** Iframe Logout Button
import IframeLogoutButton from '@/src/gygComponents/IframeLogoutButton'

import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import { AppDispatch, RootState } from '@/src/store'
import { useDispatch } from 'react-redux'
import { getSectionsLists } from '@/src/store/apps/gygSections'
import { useSelector } from 'react-redux'
import { useAuth } from '@/src/hooks/useAuth'

const VerticalLayoutWrapper = styled('div')({
  height: '100%',
  display: 'flex'
})

const MainContentWrapper = styled(Box)<BoxProps>({
  flexGrow: 1,
  minWidth: 0,
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column'
})

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  paddingTop: theme.spacing(0),
  transition: 'padding .25s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  }
}))

const VerticalLayout = (props: LayoutProps) => {
  const auth = useAuth()
  const dispatch = useDispatch<AppDispatch>()
  const router = useRouter()

  // ** Props
  const { settings, children, footerProps, contentHeightFixed, verticalLayoutProps } = props

  // ** Vars
  const { skin, navHidden, contentWidth } = settings
  const navigationBorderWidth = skin === 'bordered' ? 1 : 0
  const { navigationSize, collapsedNavigationSize } = themeConfig
  const navWidth = navigationSize
  const collapsedNavWidth = collapsedNavigationSize

  // ** States
  const [navVisible, setNavVisible] = useState<boolean>(false)
  const [subNavVisible, setSubNavVisible] = useState<boolean>(false)
  const {
    isGlobalBoolean,
    isChildPage,
    isProductPage,
    isFranchisePage,
    setChildPage,
    setProductPage,
    setFranchisePage
  } = useGlobalState()

  const isLandscape = useMediaQuery('(max-width:1200px) and (orientation: landscape)')
  const isPortrait = useMediaQuery('(max-width:1024px) and (orientation: portrait)')

  const isMobile = useMediaQuery('(max-width:540px) and (orientation: portrait)')

  // Check if this is an iframe context
  const isIframe = isIframeContext()

  // ** Toggle Functions
  const toggleNavVisibility = () => {
    setNavVisible(!navVisible)
  }

  const { sections } = useSelector((state: RootState) => state.gygSectionList)

  useEffect(() => {
    if (sections?.length === 0) {
      dispatch(getSectionsLists())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections])

  // ** Hide sub-navigation when navigating to root/main navigation pages
  // This ensures sub-navigation is hidden when jumping from sub-navigation to root navigation
  useEffect(() => {
    const pathname = router.pathname
    const { id } = router.query

    // Check if we're on a restaurant child page (has /app/restaurant/ in pathname and id exists)
    // The id check is sufficient - if id exists, we're on a dynamic route
    const isRestaurantChildPage = pathname.includes('/app/restaurant/') && id

    // Check if we're on a product page (has /app/products/ in pathname)
    const isProductPageRoute = pathname.includes('/app/products/')

    // Check if we're on a franchise child page (has /app/franchise-hub/ in pathname and id exists)
    const isFranchiseChildPage = pathname.includes('/app/franchise-hub/') && id

    // If we're not on any sub-navigation page, hide sub-navigation
    if (!isRestaurantChildPage && !isProductPageRoute && !isFranchiseChildPage) {
      // Only reset if we're actually navigating away from a sub-navigation page
      // This prevents flickering during initial page load
      if (isChildPage || isProductPage || isFranchisePage) {
        setChildPage(false)
        setProductPage(false)
        setFranchisePage(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname, router.query.id])

  return (
    <Fragment>
      <VerticalLayoutWrapper
        className={`layout-wrapper ${isIframe ? 'iframe-context' : ''}`}
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        {/* Navigation Menu */}
        {navHidden && !(navHidden && settings.lastLayout === 'horizontal') ? null : (
          <Navigation
            navWidth={navWidth}
            navVisible={navVisible}
            setNavVisible={setNavVisible}
            mainNavHidden={isGlobalBoolean}
            collapsedNavWidth={collapsedNavWidth}
            toggleNavVisibility={toggleNavVisibility}
            navigationBorderWidth={navigationBorderWidth}
            navMenuContent={verticalLayoutProps.navMenu.content}
            navMenuBranding={verticalLayoutProps.navMenu.branding}
            menuLockedIcon={verticalLayoutProps.navMenu.lockedIcon}
            verticalNavItems={verticalLayoutProps.navMenu.navItems}
            navMenuProps={verticalLayoutProps.navMenu.componentProps}
            menuUnlockedIcon={verticalLayoutProps.navMenu.unlockedIcon}
            afterNavMenuContent={verticalLayoutProps.navMenu.afterContent}
            beforeNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
            {...props}
          />
        )}

        {!isChildPage ? null : (
          <Subnavigation
            navWidth={navWidth}
            navVisible={subNavVisible}
            setNavVisible={setSubNavVisible}
            collapsedNavWidth={collapsedNavWidth}
            toggleNavVisibility={toggleNavVisibility}
            navigationBorderWidth={navigationBorderWidth}
            navMenuContent={verticalLayoutProps.navMenu.content}
            navMenuBranding={verticalLayoutProps.navMenu.branding}
            menuLockedIcon={verticalLayoutProps.navMenu.lockedIcon}
            verticalNavItems={
              auth.user?.role.name === 'Restaurant Team'
                ? verticalLayoutProps.navMenu.subNavigationRestaurant
                : isMobile
                  ? verticalLayoutProps.navMenu.subNavItemsMobile
                  : verticalLayoutProps.navMenu.subNavItems
            }
            navMenuProps={verticalLayoutProps.navMenu.componentProps}
            menuUnlockedIcon={verticalLayoutProps.navMenu.unlockedIcon}
            afterNavMenuContent={verticalLayoutProps.navMenu.afterContent}
            beforeNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
            {...props}
          />
        )}
        {!isProductPage ? null : (
          <>
            <Subnavigation
              navWidth={navWidth}
              navVisible={subNavVisible}
              setNavVisible={setSubNavVisible}
              collapsedNavWidth={collapsedNavWidth}
              toggleNavVisibility={toggleNavVisibility}
              navigationBorderWidth={navigationBorderWidth}
              navMenuContent={verticalLayoutProps.navMenu.content}
              navMenuBranding={verticalLayoutProps.navMenu.branding}
              menuLockedIcon={verticalLayoutProps.navMenu.lockedIcon}
              verticalNavItems={verticalLayoutProps.navMenu.productSubNavItems}
              navMenuProps={verticalLayoutProps.navMenu.componentProps}
              menuUnlockedIcon={verticalLayoutProps.navMenu.unlockedIcon}
              afterNavMenuContent={verticalLayoutProps.navMenu.afterContent}
              beforeNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
              {...props}
            />
          </>
        )}

        {!isFranchisePage ? null : (
          <>
            <Subnavigation
              navWidth={navWidth}
              navVisible={subNavVisible}
              setNavVisible={setSubNavVisible}
              collapsedNavWidth={collapsedNavWidth}
              toggleNavVisibility={toggleNavVisibility}
              navigationBorderWidth={navigationBorderWidth}
              navMenuContent={verticalLayoutProps.navMenu.content}
              navMenuBranding={verticalLayoutProps.navMenu.branding}
              menuLockedIcon={verticalLayoutProps.navMenu.lockedIcon}
              verticalNavItems={verticalLayoutProps.navMenu.franchiseSubNavItems}
              navMenuProps={verticalLayoutProps.navMenu.componentProps}
              menuUnlockedIcon={verticalLayoutProps.navMenu.unlockedIcon}
              afterNavMenuContent={verticalLayoutProps.navMenu.afterContent}
              beforeNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
              {...props}
            />
          </>
        )}

        <MainContentWrapper
          padding={'8px 0'}
          className='layout-content-wrapper'
          sx={{
            ...(contentHeightFixed && { maxHeight: '100vh' }),
            '& header': { position: 'relative' },
            backgroundColor: 'text.primary',
            ...(isIframe && {
              padding: 0,
              margin: 0
            })
          }}
        >
          <Card
            sx={{
              background: '#f8f8f8',
              height: '100%',
              overflow: 'auto',
              borderTopLeftRadius: isPortrait || isLandscape ? 0 : '16px',
              borderBottomLeftRadius: isPortrait || isLandscape ? 0 : '16px',
              borderTopRightRadius: '0',
              borderBottomRightRadius: '0',
              borderColor: 'text.primary',
              ...(isIframe && {
                borderRadius: 0,
                border: 'none',
                boxShadow: 'none'
              })
            }}
          >
            {/* AppBar Component */}
            {/* Sajid remove top header as per new design */}
            {(isPortrait || isLandscape) && !isIframe && (
              <AppBar
                toggleNavVisibility={toggleNavVisibility}
                navVisible={navVisible}
                appBarContent={verticalLayoutProps.appBar?.content}
                appBarProps={verticalLayoutProps.appBar?.componentProps}
                {...props}
              />
            )}

            {/* Content */}
            <ContentWrapper
              className='layout-page-content'
              sx={{
                ...(contentHeightFixed && {
                  overflow: 'hidden',
                  '& > :first-of-type': { height: '100%' }
                }),
                ...(contentWidth === 'boxed' && {
                  mx: 'auto',
                  '@media (min-width:1440px)': { maxWidth: 1440 },
                  '@media (min-width:1200px)': { maxWidth: '100%' }
                })
              }}
            >
              {children}
              {isIframe && <IframeLogoutButton />}
            </ContentWrapper>

            {/* Footer Component */}
            {!isIframe && <Footer footerStyles={footerProps?.sx} footerContent={footerProps?.content} {...props} />}
          </Card>
        </MainContentWrapper>
      </VerticalLayoutWrapper>

      {/* Scroll to top button */}
      {/* {scrollToTop ? (
        scrollToTop(props)
      ) : (
        <ScrollToTop className='mui-fixed'>
          <Fab color='primary' size='small' aria-label='scroll back to top'>
            <Icon icon='tabler:arrow-up' />
          </Fab>
        </ScrollToTop>
      )} */}
    </Fragment>
  )
}

export default VerticalLayout
